import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import { Loader2, Save, Plus, Trash2 } from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';
import TranslateButton from '../../../components/admin/TranslateButton';
import AutoTranslateIndicator from '../../../components/admin/AutoTranslateIndicator';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    label_pt: 'SERVIÇOS',
    label_en: 'SERVICES',
    heading_pt: '',
    heading_en: '',
    subheading_pt: '',
    subheading_en: '',
    services: []
  });

  useEffect(() => {
    loadServicesData();
  }, []);

  const loadServicesData = async () => {
    setLoading(true);
    try {
      const [ptResponse, enResponse] = await Promise.all([
        axios.get(`${API_URL}/api/homepage-ml/services?lang=pt`),
        axios.get(`${API_URL}/api/homepage-ml/services?lang=en`)
      ]);

      const ptData = ptResponse.data;
      const enData = enResponse.data;

      setFormData({
        label_pt: ptData.label || 'SERVIÇOS',
        label_en: enData.label || 'SERVICES',
        heading_pt: ptData.heading || '',
        heading_en: enData.heading || '',
        subheading_pt: ptData.subheading || '',
        subheading_en: enData.subheading || '',
        services: ptData.services || []
      });
    } catch (error) {
      console.error('Failed to load services:', error);
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceChange = (index, field, value) => {
    setFormData(prev => {
      const newServices = [...prev.services];
      newServices[index] = { ...newServices[index], [field]: value };
      return { ...prev, services: newServices };
    });
  };

  const addService = () => {
    setFormData(prev => ({
      ...prev,
      services: [
        ...prev.services,
        {
          id: prev.services.length + 1,
          icon: 'briefcase',
          title_pt: '',
          title_en: '',
          description_pt: '',
          description_en: '',
          link: '',
          visible: true,
          order: prev.services.length + 1
        }
      ]
    }));
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API_URL}/api/homepage-ml/services`, formData);
      toast({ title: "Sucesso", description: "Secção Services guardada" });
    } catch (error) {
      toast({ variant: "destructive", title: "Erro", description: "Falha ao guardar" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <BackButton />

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Secção Services</CardTitle>
            <CardDescription>Configure os serviços oferecidos (PT/EN)</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Portuguese */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4 text-[#00B4D8]">🇵🇹 Português</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Label (PT)</label>
                    <input
                      type="text"
                      value={formData.label_pt}
                      onChange={(e) => handleInputChange('label_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Heading (PT)</label>
                    <input
                      type="text"
                      value={formData.heading_pt}
                      onChange={(e) => handleInputChange('heading_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subheading (PT)</label>
                    <textarea
                      value={formData.subheading_pt}
                      onChange={(e) => handleInputChange('subheading_pt', e.target.value)}
                      rows={2}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* English */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#00B4D8]">🇬🇧 English</h3>
                  <TranslateButton
                    sourceTexts={{
                      label: formData.label_pt,
                      heading: formData.heading_pt,
                      subheading: formData.subheading_pt
                    }}
                    onTranslated={(translated) => {
                      setFormData(prev => ({
                        ...prev,
                        label_en: translated.label || '',
                        heading_en: translated.heading || '',
                        subheading_en: translated.subheading || ''
                      }));
                    }}
                  />
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Label (EN)</label>
                    <input
                      type="text"
                      value={formData.label_en}
                      onChange={(e) => handleInputChange('label_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Heading (EN)</label>
                    <input
                      type="text"
                      value={formData.heading_en}
                      onChange={(e) => handleInputChange('heading_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subheading (EN)</label>
                    <textarea
                      value={formData.subheading_en}
                      onChange={(e) => handleInputChange('subheading_en', e.target.value)}
                      rows={2}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Services List */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Serviços</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addService}>
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Serviço
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.services.map((service, idx) => (
                    <Card key={idx} className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-medium">Serviço {idx + 1}</h4>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeService(idx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={service.title_pt}
                          onChange={(e) => handleServiceChange(idx, 'title_pt', e.target.value)}
                          placeholder="Título PT"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={service.title_en}
                          onChange={(e) => handleServiceChange(idx, 'title_en', e.target.value)}
                          placeholder="Title EN"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={service.description_pt}
                          onChange={(e) => handleServiceChange(idx, 'description_pt', e.target.value)}
                          placeholder="Descrição PT"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={service.description_en}
                          onChange={(e) => handleServiceChange(idx, 'description_en', e.target.value)}
                          placeholder="Description EN"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={service.icon}
                          onChange={(e) => handleServiceChange(idx, 'icon', e.target.value)}
                          placeholder="Ícone (ex: building)"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={service.link}
                          onChange={(e) => handleServiceChange(idx, 'link', e.target.value)}
                          placeholder="Link (ex: /services/...)"
                          className="px-3 py-2 border rounded"
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="pt-6 border-t space-y-4">
                <AutoTranslateIndicator />
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => navigate('/admin/homepage')}>Cancelar</Button>
                  <Button type="submit" disabled={saving} className="bg-[#00B4D8]" data-testid="services-save-button">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</> : <><Save className="w-4 h-4 mr-2" />Guardar</>}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ServicesForm;